import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import {
  fetchPaginatedAppointments,
  fetchPaginatedGeneralRequests,
} from '../../services/appointments';
import { SortableKeys } from '../../types/appointments';
import { SortValues } from '../../types/ui';

function useGetRequests({
  status: passedStatus,
  enabled = true,
  sort,
  employeeIds,
  isCanceledByCustomer,
}: {
  status: 'ACCEPTED' | 'REJECTED' | 'REQUESTED' | 'ARCHIVED' | 'CANCELED';
  enabled?: boolean;
  sort?: SortValues<SortableKeys>;
  employeeIds?: string[];
  isCanceledByCustomer?: boolean;
}) {
  const { user } = useUser();

  const sortedEmployeeIds = employeeIds?.sort();

  const status = isCanceledByCustomer ? 'CANCELED' : passedStatus;

  // A declined offer is the artist's turn again, so it moves to new requests
  const offeredTimesDeclined =
    status === 'REQUESTED'
      ? ('INCLUDE' as const)
      : status === 'ACCEPTED'
      ? ('EXCLUDE' as const)
      : undefined;

  const queryKey = React.useMemo(
    () => [
      'requests',
      {
        status,
        offeredTimesDeclined,
        employeeId: user?.employee?.employeeId,
        businessId: user?.business?.id,
        role: user?.role,
        sortOrder: sort?.order,
        sortOrderBy: sort?.orderBy,
        employeeIds: sortedEmployeeIds,
        isCanceledByCustomer,
      },
    ],
    [
      status,
      offeredTimesDeclined,
      user?.employee?.employeeId,
      user?.business?.id,
      user?.role,
      sort?.order,
      sort?.orderBy,
      sortedEmployeeIds,
      isCanceledByCustomer,
    ],
  );

  const {
    data,
    isLoading,
    isRefetching,
    isFetching,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isPlaceholderData,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async ({ pageParam: cursors }) => {
      if (!user) throw new Error('No user found');

      const appointments = await fetchPaginatedAppointments({
        businessId: user.business.id,
        status,
        sort,
        employeeIds: sortedEmployeeIds,
        isCanceledByCustomer,
        offeredTimesDeclined,
        cursor: cursors?.appointments,
      });

      const generalRequests =
        user.role === 'SHOP_MANAGER' &&
        user.generalRequestOwner &&
        (status === 'REQUESTED' || status === 'ARCHIVED')
          ? await fetchPaginatedGeneralRequests({
              businessId: user.business.id,
              sort,
              deletedOnly: status === 'ARCHIVED',
              excludeDeleted: status !== 'ARCHIVED',
              employeeIds: sortedEmployeeIds,
              cursor: cursors?.generalRequests,
            })
          : undefined;

      return {
        appointments,
        generalRequests,
      };
    },
    enabled: !!user && enabled,
    // after 1 minute, the data is considered stale and will be refetched when it's queried (on mount, or when refetch is called)
    staleTime: 60000,
    initialPageParam: {
      appointments: undefined as string | undefined,
      generalRequests: undefined as string | undefined,
    },
    getNextPageParam: lastPage => {
      const noMore =
        !lastPage.appointments.meta.hasMore &&
        !lastPage.generalRequests?.meta.hasMore;
      if (noMore) return undefined;
      return {
        appointments: lastPage.appointments.meta.hasMore
          ? lastPage.appointments.meta.cursor
          : undefined,
        generalRequests: lastPage.generalRequests?.meta.hasMore
          ? lastPage.generalRequests.meta.cursor
          : undefined,
      };
    },
  });

  const appointments = React.useMemo(
    () => data?.pages?.flatMap(page => page.appointments.appointments) ?? [],
    [data?.pages],
  );
  const generalRequests = React.useMemo(
    () =>
      data?.pages?.flatMap(
        page => page.generalRequests?.generalRequests ?? [],
      ) ?? [],
    [data?.pages],
  );

  // sort manually if general requests are included - 2 separate tables
  const combinedAppointments = React.useMemo(() => {
    if (!generalRequests) return appointments ?? [];

    const tempAppointments = [...appointments, ...generalRequests];

    if (!sort?.order || !sort?.orderBy)
      return tempAppointments.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ); // If no sort is provided, sort by the default createdAt descending

    return tempAppointments.sort((a, b) => {
      const first = sort.order === 'asc' ? a : b;
      const second = sort.order === 'asc' ? b : a;
      if (sort.orderBy === 'createdAt') {
        return new Date(first.createdAt) < new Date(second.createdAt) ? -1 : 1;
      }
      if (sort.orderBy === 'customer') {
        return first.customer.firstName.localeCompare(
          second.customer.firstName,
        );
      }
      if (sort.orderBy === 'employee') {
        const firstEmployeeName =
          'employee' in first && first.employee
            ? first.employee?.displayName || first.employee?.firstName
            : 'Any Artist';
        const secondEmployeeName =
          'employee' in second && second.employee
            ? second.employee?.displayName || second.employee?.firstName
            : 'Any Artist';

        return firstEmployeeName.localeCompare(secondEmployeeName);
      }
      if (sort.orderBy === 'tattooLocation') {
        return (first.tattooLocation || '').localeCompare(
          second.tattooLocation || '',
        );
      }
      if (sort.orderBy === 'service') {
        const firstCategory =
          'serviceCategory' in first ? first.serviceCategory : 'TATTOO';
        const secondCategory =
          'serviceCategory' in second ? second.serviceCategory : 'TATTOO';
        return (firstCategory || 'TATTOO').localeCompare(
          secondCategory || 'TATTOO',
        );
      }
      return 0;
    });
  }, [appointments, generalRequests, sort?.order, sort?.orderBy]);

  const totalCount = React.useMemo(
    () =>
      data?.pages
        ? (data.pages[data.pages.length - 1]?.appointments?.meta?.count ?? 0) +
          (data.pages[data.pages.length - 1]?.generalRequests?.meta?.count ?? 0)
        : 0,
    [data?.pages],
  );

  return {
    appointments,
    generalRequests,
    combinedAppointments,
    totalCount,
    isLoading,
    isRefetching,
    isFetching,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isPlaceholderData,
  };
}

export default useGetRequests;
